/**
 * In-memory JWT store for the legacy doctor SPA.
 *
 * K4 Aşama 3 (owner, 2026-10-13): "tokenları localStorage'da tutma".
 *
 * The access and refresh tokens live in this module only, so they disappear
 * when the page is closed or reloaded. Nothing here writes a token to
 * localStorage, sessionStorage or a cookie, and nothing here logs one.
 *
 * A tab without tokens (a fresh load, a reload, a new tab) asks the server
 * for a fresh pair with POST /api2/v2/auth/login/ {"session": true} -- the
 * login door's session mode (owner, 2026-10-14; it was POST
 * /kimlik/v1/token/, which stays on the server for older builds). The
 * page's shell (frontend/templates/frontend/index.html) says whether the
 * browser has a Django session at all (window.__ortheroSession); when it
 * says no, a tab that never held a token does not call the server (no 401
 * on the sign-in page). The browser's Django session
 * cookie is the only proof of who it is; every credential login and every
 * staff "login as" leaves such a session behind. The contract with
 * kimlik/views.py (session_login_response, the same contract as
 * SessionTokenView):
 *   - JSON body {"session": true}, same origin, credentials 'same-origin', and the header
 *     X-CSRFToken = the 'csrftoken' cookie (frontend.views.index always sets
 *     that cookie);
 *   - 200 -> {"serviceToken": <access>, "refresh": <refresh>};
 *   - any other answer means "no session": the in-memory tokens are cleared
 *     and the caller takes the normal logout path.
 *
 * Both server calls made here use fetch, not axios, so the app's axios
 * interceptors (Authorization header, 401 retry, 500 redirect) never see
 * them and can never retry them. No Authorization header is sent to the
 * session endpoint either: seffaf.middleware.JWTTokenMiddleware would swap
 * request.user for the Bearer's account, and the server would refuse.
 *
 * One tab, one account: the first token this page stores names the tab's
 * account for the rest of the page's life (clearTokens() does not forget
 * it; only a page load does). A refresh that falls back to the session
 * refuses a pair for any other account, so the tab takes the logout path
 * instead of silently becoming the other account, which is the account
 * mix-up (O-1) that K4 fixes. Every legitimate account change in the SPA
 * (login, logout, staff "login as") ends in a page load anyway.
 *
 * Further down: a signed-in tab follows its account across tabs (another
 * tab's logout or sign-in as someone else sends it to the sign-in page), a
 * staff "login as" the session did not take is remembered for the tab by
 * account ID (sessionStorage, never a token), and an entry link may name the
 * account it expects ('?expect=<id>').
 */

import jwtDecode from 'jwt-decode';

// Owner, 2026-10-14: the login door's session mode (kimlik.views.
// session_login_response). Was '/kimlik/v1/token/'.
const SESSION_TOKEN_URL = '/api2/v2/auth/login/';
const SESSION_TOKEN_BODY = JSON.stringify({ session: true });
const REFRESH_URL = '/api2/v2/login/refresh/';
const CSRF_COOKIE_NAME = 'csrftoken';

// localStorage keys where the SPA kept the tokens before K4 Aşama 3.
// purgeStoredTokens() removes them, which clears what existing users still
// have stored.
const LEGACY_TOKEN_KEYS = ['serviceToken', 'refreshToken'];

let accessToken = null;
let refreshToken = null;

// The account (user_id claim, as a string) this page holds tokens for; see
// "One tab, one account" above.
let tabAccount = null;

// Single-flight promises: concurrent callers share one network call.
let sessionFlight = null;
let refreshFlight = null;

const tokenClaims = (token) => {
    if (!token) {
        return null;
    }
    try {
        return jwtDecode(token);
    } catch (e) {
        return null;
    }
};

// The account id ('user_id' claim, SIMPLE_JWT USER_ID_CLAIM) as a string, or
// null when the token cannot be read. The signature is not checked; the id
// only tells which account a token claims to be for.
export const tokenUserId = (token) => {
    const claims = tokenClaims(token);
    return claims && claims.user_id != null ? String(claims.user_id) : null;
};

// True while the token's 'exp' is in the future.
export const isTokenLive = (token) => {
    const claims = tokenClaims(token);
    return !!claims && typeof claims.exp === 'number' && claims.exp > Date.now() / 1000;
};

export const getAccessToken = () => accessToken;

export const getRefreshToken = () => refreshToken;

export const setAccessToken = (access) => {
    accessToken = access || null;
    const userId = tokenUserId(accessToken);
    if (userId !== null) {
        tabAccount = userId;
    }
};

export const setRefreshToken = (refresh) => {
    refreshToken = refresh || null;
};

export const setTokens = (access, refresh) => {
    setAccessToken(access);
    setRefreshToken(refresh);
};

// Drops both tokens. The tab's account is kept on purpose (see above).
export const clearTokens = () => {
    accessToken = null;
    refreshToken = null;
};

// Removes the token keys that earlier builds left in localStorage. The app
// calls it once at start (App.js). Storage may be blocked, so failures are
// ignored.
export const purgeStoredTokens = () => {
    LEGACY_TOKEN_KEYS.forEach((key) => {
        try {
            window.localStorage.removeItem(key);
        } catch (e) {
            // Storage blocked: nothing stored, nothing to remove.
        }
    });
};

// An Error for a refused call. It carries a short code ('no_session',
// 'session_ended', 'cross_site', 'account_changed', ...) and the HTTP status,
// never a token.
const tokenError = (code, status) => {
    const error = new Error(`kimlik token: ${code}`);
    error.code = code;
    error.status = status;
    return error;
};

// The HTTP status a failed step carries: a tokenError's 'status', an axios
// error's 'response.status', or 0 when there is none (no answer at all, or a
// refusal the tab made on its own).
const failureStatus = (error) => {
    if (!error) {
        return 0;
    }
    if (typeof error.status === 'number') {
        return error.status;
    }
    return (error.response && error.response.status) || 0;
};

/**
 * K4 Aşama 3 (owner, 2026-10-13): true when a failed sign-in step means the
 * server is done with this browser's sign-in, so the sign-out path may clear
 * the localStorage the tabs share. A clear is a browser-wide sign-out now:
 * every tab that follows its account (followAccountAcrossTabs below) reads
 * it as "signed out" and leaves for the sign-in page.
 *
 * True for a server refusal: 401 no_session / session_ended, a 403, a 404
 * from the "login as" handoff, or the 200 'account_changed' that
 * refreshAccessToken raises when the session now serves another account.
 *
 * False for a failure that belongs to this tab alone, while the session may
 * well be alive: no answer (network, status 0), a 429 from the throttle, a
 * 5xx or the maintenance 503, and a refusal the tab made on its own without
 * any status (JWTContext's 'staff_landing', 'login_as_changed', the
 * '?expect' 'account_changed'). Clearing storage for those would send every
 * other tab to the sign-in page and back, losing unsaved work; a 429 would
 * even feed itself, since each bounced tab calls the throttled endpoint again.
 */
export const failureEndsSignIn = (error) => {
    const status = failureStatus(error);
    if (!status || status === 429 || status >= 500) {
        return false;
    }
    return true;
};

const readCookie = (name) => {
    try {
        const parts = document.cookie ? document.cookie.split(';') : [];
        for (let i = 0; i < parts.length; i += 1) {
            const part = parts[i].trim();
            const eq = part.indexOf('=');
            if (eq > 0 && part.slice(0, eq) === name) {
                return decodeURIComponent(part.slice(eq + 1));
            }
        }
    } catch (e) {
        // Cookies unreadable: the server answers with its CSRF 403.
    }
    return '';
};

const readJson = (response) => response.json().catch(() => null);

// Runs `start` once for all concurrent callers. `getFlight`/`setFlight`
// hold the shared promise, which is dropped when it settles.
const singleFlight = (getFlight, setFlight, start) => {
    const running = getFlight();
    if (running) {
        return running;
    }
    const flight = start();
    setFlight(flight);
    const done = () => {
        if (getFlight() === flight) {
            setFlight(null);
        }
    };
    flight.then(done, done);
    return flight;
};

// True when the page's shell says this browser has no Django session
// (window.__ortheroSession === false, rendered by frontend.views.index).
// Only trusted while this page has never held a token: after a sign-in in
// this page the shell's answer is stale (owner, 2026-10-14).
const shellSaysNoSession = () => {
    if (tabAccount !== null) {
        return false;
    }
    try {
        return window.__ortheroSession === false;
    } catch (e) {
        return false;
    }
};

// POST /api2/v2/auth/login/ {"session": true}: resolves {serviceToken,
// refresh} on 200, rejects with a tokenError otherwise. Stores nothing.
// Without a session (per the shell) it rejects as the server would, with no
// network call.
const postSessionToken = () => {
    if (shellSaysNoSession()) {
        return Promise.reject(tokenError('no_session', 401));
    }
    return fetch(SESSION_TOKEN_URL, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRFToken': readCookie(CSRF_COOKIE_NAME),
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: SESSION_TOKEN_BODY,
    }).then(
        (response) =>
            readJson(response).then((body) => {
                if (response.status !== 200 || !body || !body.serviceToken) {
                    throw tokenError((body && typeof body.code === 'string' && body.code) || 'no_session', response.status);
                }
                return { serviceToken: body.serviceToken, refresh: body.refresh || null };
            }),
        () => {
            throw tokenError('network', 0);
        },
    );
};

const fetchSessionPair = () =>
    singleFlight(
        () => sessionFlight,
        (flight) => {
            sessionFlight = flight;
        },
        postSessionToken,
    );

// POST /api2/v2/login/refresh/ with the given refresh token: resolves the
// rotated pair, rejects with a tokenError otherwise. Stores nothing.
const postRefresh = (refresh) =>
    fetch(REFRESH_URL, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh }),
    }).then(
        (response) =>
            readJson(response).then((body) => {
                if (!response.ok || !body || !body.access) {
                    throw tokenError((body && typeof body.code === 'string' && body.code) || 'refresh_refused', response.status);
                }
                // ROTATE_REFRESH_TOKENS is on; keep the old one if none came back.
                return { serviceToken: body.access, refresh: body.refresh || refresh };
            }),
        () => {
            throw tokenError('network', 0);
        },
    );

/**
 * A fresh pair from this browser's Django session. Stores it and resolves
 * {serviceToken, refresh}; on any refusal clears the in-memory tokens and
 * rejects (the caller takes the logout path). Concurrent calls share one
 * request.
 */
export const requestSessionTokens = () =>
    fetchSessionPair().then(
        (pair) => {
            setTokens(pair.serviceToken, pair.refresh);
            return pair;
        },
        (error) => {
            clearTokens();
            throw error;
        },
    );

/**
 * True when this browser's Django session serves the account `userId`, i.e.
 * POST /api2/v2/auth/login/ {"session": true} answers 200 with a token for that account. Stores
 * nothing and never rejects. Used after the staff "login as" (hiJack)
 * handoff to decide whether a reload would come back as the same account.
 */
export const sessionServesAccount = (userId) => {
    if (userId == null) {
        return Promise.resolve(false);
    }
    return fetchSessionPair().then(
        (pair) => tokenUserId(pair.serviceToken) === String(userId),
        () => false,
    );
};

// The session's pair, refused when it is for another account than the
// tab's (a tab that holds no account yet takes any).
const sessionPairForTab = () =>
    fetchSessionPair().then((pair) => {
        if (tabAccount !== null && tokenUserId(pair.serviceToken) !== tabAccount) {
            throw tokenError('account_changed', 200);
        }
        return pair;
    });

/**
 * A new access token for this tab. Concurrent calls share one attempt.
 *
 * Tries POST /api2/v2/login/refresh/ with the in-memory refresh token and
 * stores the rotated pair. When that is refused (or there is no refresh
 * token), falls back to the session (POST /api2/v2/auth/login/ {"session": true}), but only for
 * the account this tab already holds ('account_changed' otherwise).
 * Resolves the new access token; on failure clears the in-memory tokens and
 * rejects (logout path).
 */
export const refreshAccessToken = () =>
    singleFlight(
        () => refreshFlight,
        (flight) => {
            refreshFlight = flight;
        },
        () => {
            const current = refreshToken;
            const attempt = current ? postRefresh(current) : Promise.reject(tokenError('no_refresh', 0));
            return attempt
                .catch(() => sessionPairForTab())
                .then(
                    (pair) => {
                        setTokens(pair.serviceToken, pair.refresh);
                        return pair.serviceToken;
                    },
                    (error) => {
                        clearTokens();
                        throw error;
                    },
                );
        },
    );

/**
 * A usable access token for page start (JWTContext init): the in-memory one
 * while it is live; a refresh (with the account check) when this tab already
 * holds an account; otherwise, in a fresh tab, a pair from the session.
 * Rejects when none can be had (logout path).
 */
export const ensureAccessToken = () => {
    if (accessToken && isTokenLive(accessToken)) {
        return Promise.resolve(accessToken);
    }
    if (accessToken || refreshToken || tabAccount !== null) {
        return refreshAccessToken();
    }
    return requestSessionTokens().then((pair) => pair.serviceToken);
};

// ---------------------------------------------------------------------------
// Following the tab's account across tabs
// ---------------------------------------------------------------------------
//
// K4 Aşama 3 (owner, 2026-10-13). Before this change every tab read the same
// 'serviceToken' / 'refreshToken' keys, so a sign-out in one tab took the
// tokens away from all of them, and a sign-in as another account in one tab
// switched the token under every other tab while their screens still showed
// the first account (O-1). Now each tab holds its own pair, and while
// AUTH_SESSION_BINDING is 'off' or 'shadow' the server does not stop the
// other tabs either: their refresh still rotates after a logout elsewhere.
//
// What the tabs still share is the 'userData' record: logout() removes it,
// every sign-in writes it (with the account id the token is for), and the
// sign-out paths clear localStorage. A tab that follows its account leaves
// for the sign-in page as soon as another tab removes that record, clears
// localStorage, or writes a record for another account. That page load asks
// the session again, so the tab either signs in afresh as whoever the session
// now serves (a fresh page, as with any reload) or shows the sign-in page; it
// never keeps one account's tokens under another account's record. This works
// the same in every binding mode. Writes of the same account's record (the
// badge counters, a profile edit, another tab's reload) are ignored.
//
// Only the top window follows: the SPA frames that staff pages embed stay
// passive, as the inline 'orthero-auth' script in index.html does. A tab
// follows only while it is signed in (JWTContext starts it after a successful
// sign-in and stops it in its sign-out path), so tabs that are already on the
// sign-in page never bounce each other.

const USER_DATA_KEY = 'userData';
const SIGN_IN_PATH = '/new/login';

// The account id (as a string) this tab follows, or null.
let followedAccount = null;
let followListenerAdded = false;
let leavingForSignIn = false;

// The account id a stored 'userData' value names, or null (no record, '{}',
// broken JSON, no id).
const recordAccountId = (raw) => {
    if (!raw) {
        return null;
    }
    try {
        const data = JSON.parse(raw);
        return data && typeof data === 'object' && data.id != null && data.id !== '' ? String(data.id) : null;
    } catch (e) {
        return null;
    }
};

const isLocalStorageEvent = (event) => {
    try {
        return !event.storageArea || event.storageArea === window.localStorage;
    } catch (e) {
        return false;
    }
};

/**
 * True when a 'storage' event (fired by ANOTHER tab or frame) means that
 * `accountId`'s sign-in is over in this browser: the 'userData' record was
 * removed, localStorage was cleared (key null), or the record now names
 * another account. Every other key, and a record for the same account,
 * answers false.
 */
export const storageEventEndsAccount = (event, accountId) => {
    if (!event || accountId == null || !isLocalStorageEvent(event)) {
        return false;
    }
    if (event.key !== null && event.key !== USER_DATA_KEY) {
        return false;
    }
    const recordId = event.key === null ? null : recordAccountId(event.newValue);
    return recordId !== String(accountId);
};

const onStorageChange = (event) => {
    if (followedAccount === null || leavingForSignIn) {
        return;
    }
    if (!storageEventEndsAccount(event, followedAccount)) {
        return;
    }
    leavingForSignIn = true;
    console.info('[auth] another tab signed this account out or in as another account; leaving for the sign-in page');
    clearTokens();
    window.location.replace(SIGN_IN_PATH);
};

const isTopWindow = () => {
    try {
        return window.self === window.top;
    } catch (e) {
        return false;
    }
};

/**
 * Starts (or moves) following `accountId` across tabs; see above. Called by
 * JWTContext after each successful sign-in of a top-level page. Idempotent.
 */
export const followAccountAcrossTabs = (accountId) => {
    if (!isTopWindow()) {
        return;
    }
    followedAccount = accountId == null || accountId === '' ? null : String(accountId);
    if (followedAccount === null || followListenerAdded) {
        return;
    }
    try {
        window.addEventListener('storage', onStorageChange);
        followListenerAdded = true;
    } catch (e) {
        // No storage events in this browser: the tab works as before.
    }
};

// Stops following (the tab has signed out and is on the sign-in page).
export const stopFollowingAccount = () => {
    followedAccount = null;
};

// ---------------------------------------------------------------------------
// A staff "login as" the session did not take
// ---------------------------------------------------------------------------
//
// K4 Aşama 3 (owner, 2026-10-13). After the staff "Giriş Yap" / "Portal Giriş
// Yap" handoff, JWTContext reloads the tab only when the browser's Django
// session serves the account it was handed (sessionServesAccount). When it
// does not (the staff view refused, or the keys were left behind), the tab
// stays on that account's in-memory tokens. A later reload of that tab would
// otherwise get the session's pair, which is the staff member's own, and
// silently turn the tab into the staff account on whatever page it shows.
// The tab therefore remembers the handed-over account's ID (never a token)
// in sessionStorage, which belongs to this tab alone and survives its
// reloads; while that mark is there, a pair for any other account is
// refused and the tab shows the sign-in page instead. A credential login, a
// logout and the sign-out path remove the mark.

const LOGIN_AS_ACCOUNT_KEY = 'loginAsAccount';

export const rememberLoginAsAccount = (accountId) => {
    if (accountId == null || accountId === '') {
        return;
    }
    try {
        window.sessionStorage.setItem(LOGIN_AS_ACCOUNT_KEY, String(accountId));
    } catch (e) {
        // Storage blocked: the tab keeps today's behaviour.
    }
};

export const forgetLoginAsAccount = () => {
    try {
        window.sessionStorage.removeItem(LOGIN_AS_ACCOUNT_KEY);
    } catch (e) {
        // Storage blocked: nothing stored, nothing to remove.
    }
};

/**
 * True when this tab carries a "login as" mark for an account other than the
 * one `token` is for (a token that cannot be read counts as another account).
 * False when there is no mark.
 */
export const loginAsRefusesToken = (token) => {
    let expected = null;
    try {
        expected = window.sessionStorage.getItem(LOGIN_AS_ACCOUNT_KEY);
    } catch (e) {
        expected = null;
    }
    if (!expected) {
        return false;
    }
    return tokenUserId(token) !== expected;
};

// ---------------------------------------------------------------------------
// An entry link that names the account it expects
// ---------------------------------------------------------------------------
//
// K4 Aşama 3 (owner, 2026-10-13). A link into the SPA from another app of
// this site (FLUX's "Doktor portalı") may say which account it is opening
// the portal for, as '?expect=<account id>'. The session decides who the
// portal signs in as, so when the session serves a different account the
// page shows the sign-in page instead of that other account's portal. A
// page without the parameter is not affected.

const EXPECT_ACCOUNT_PARAM = 'expect';

/**
 * True when the page URL carries '?expect=<id>' and `token` is for another
 * account (or cannot be read). False when the parameter is absent or empty.
 */
export const expectedAccountRefusesToken = (token) => {
    let expected = null;
    try {
        expected = new URLSearchParams(window.location.search).get(EXPECT_ACCOUNT_PARAM);
    } catch (e) {
        expected = null;
    }
    if (!expected) {
        return false;
    }
    return tokenUserId(token) !== String(expected).trim();
};
